import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Edit3, Link2, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import { SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';

// Admin → Mappings. CRUD for the table the IPN processor uses to translate
// Launchpad `product_id` (+ optional `offer_id`) into the internal RankOnAI
// Plan.slug that gets granted on purchase.
//
// Inline-editable rows; "Add mapping" form anchored at the top.
// If navigated to with ?provider=...&productId=...&offerId=...&label=... query
// params (Sales page's "Add mapping" CTA from unmapped_sku errors), the
// create form is pre-filled.
export default function MappingsPage() {
  const qc = useQueryClient();
  const location = useLocation();
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState('');
  const [active, setActive] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState(emptyDraft());
  const PAGE_SIZE = 50;

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => setPage(1), [query, provider, active]);

  // Pre-fill from Sales-page "Add mapping" CTA.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('provider') || sp.get('productId')) {
      setCreating(true);
      setCreateDraft({
        provider: sp.get('provider') || 'launchpad',
        externalProductId: sp.get('productId') || '',
        externalOfferId: sp.get('offerId') || '',
        externalLabel: sp.get('label') || '',
        internalSkuSlug: '',
        notes: '',
        isActive: true,
      });
    }
    // intentionally only when the search string changes:
  }, [location.search]);

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (provider) params.set('provider', provider);
  if (active) params.set('isActive', active);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));

  const mappingsQuery = useQuery({
    queryKey: ['admin-mappings', query, provider, active, page],
    queryFn: () => api.get(`/api/admin/product-mappings?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const availableSkus = useMemo(
    () => mappingsQuery.data?.availableSkus || [],
    [mappingsQuery.data?.availableSkus],
  );

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/admin/product-mappings', body).then((r) => r.data),
    onSuccess: () => {
      toast.success('Mapping created');
      setCreating(false);
      setCreateDraft(emptyDraft());
      qc.invalidateQueries({ queryKey: ['admin-mappings'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error?.message || 'Create failed');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      api.put(`/api/admin/product-mappings/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      toast.success('Mapping updated');
      setEditingId(null);
      setEditDraft({});
      qc.invalidateQueries({ queryKey: ['admin-mappings'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error?.message || 'Update failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/product-mappings/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Mapping deleted');
      qc.invalidateQueries({ queryKey: ['admin-mappings'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error?.message || 'Delete failed');
    },
  });

  function beginEdit(row) {
    setEditingId(row._id);
    setEditDraft({
      provider: row.provider,
      externalProductId: row.externalProductId,
      externalOfferId: row.externalOfferId || '',
      externalLabel: row.externalLabel || '',
      internalSkuSlug: row.internalSkuSlug,
      notes: row.notes || '',
      isActive: row.isActive,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft({});
  }

  function commitEdit(row) {
    const body = {};
    for (const k of Object.keys(editDraft)) {
      if (editDraft[k] !== row[k]) body[k] = editDraft[k];
    }
    if (Object.keys(body).length === 0) {
      cancelEdit();
      return;
    }
    updateMutation.mutate({ id: row._id, body });
  }

  function commitCreate() {
    if (!createDraft.externalProductId || !createDraft.internalSkuSlug) {
      toast.error('Product ID and internal SKU are required');
      return;
    }
    createMutation.mutate(createDraft);
  }

  function maybeDelete(row) {
    if (
      !confirm(
        `Delete this mapping?\n\n${row.provider} ${row.externalProductId}${
          row.externalOfferId ? ` / offer ${row.externalOfferId}` : ''
        } → ${row.internalSkuSlug}\n\nThis breaks future IPN matching unless you replace it.`,
      )
    )
      return;
    deleteMutation.mutate(row._id);
  }

  const rows = mappingsQuery.data?.mappings || [];
  const total = mappingsQuery.data?.total ?? 0;
  const hasActiveFilter = Boolean(query || provider || active);

  return (
    <div className="card">
      <div className="section-header flex-wrap gap-3">
        <div className="section-title">
          <span className="section-icon">
            <Link2 className="w-4 h-4" />
          </span>
          Product mappings
          {mappingsQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10 max-w-xs"
              placeholder="Search product ID or SKU…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <select
            className="input max-w-[140px]"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="">All providers</option>
            <option value="launchpad">Launchpad</option>
            <option value="warriorplus">WarriorPlus</option>
            <option value="jvzoo">JVZoo</option>
            <option value="thrivecart">ThriveCart</option>
            <option value="manual">Manual</option>
          </select>
          <select
            className="input max-w-[120px]"
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="">All status</option>
            <option value="yes">Active</option>
            <option value="no">Inactive</option>
          </select>
          {hasActiveFilter && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setProvider('');
                setActive('');
              }}
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          {!creating && (
            <button type="button" className="btn-primary text-xs" onClick={() => setCreating(true)}>
              <Plus className="w-3 h-3" /> Add mapping
            </button>
          )}
        </div>
      </div>

      {/* Helper hint when zero mappings + zero filter */}
      {!hasActiveFilter && !rows.length && !mappingsQuery.isLoading && !creating && (
        <p className="text-xs text-ink-500 pb-3">
          No mappings yet — incoming IPN events will land as <code>unmapped_sku</code> errors until
          you add at least one mapping per Launchpad <code>product_id</code> you want to honor.
        </p>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs text-ink-500 uppercase tracking-wide">
              <th className="text-left font-medium py-2 px-3">Provider</th>
              <th className="text-left font-medium py-2 px-3">External product ID</th>
              <th className="text-left font-medium py-2 px-3">Offer ID</th>
              <th className="text-left font-medium py-2 px-3">Label / notes</th>
              <th className="text-left font-medium py-2 px-3">→ Internal SKU</th>
              <th className="text-left font-medium py-2 px-3">Active</th>
              <th className="text-right font-medium py-2 px-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {creating && (
              <EditRow
                draft={createDraft}
                setDraft={setCreateDraft}
                availableSkus={availableSkus}
                isCreate
                onSave={commitCreate}
                onCancel={() => {
                  setCreating(false);
                  setCreateDraft(emptyDraft());
                }}
                pending={createMutation.isPending}
              />
            )}

            {mappingsQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="p-0">
                  <SkeletonTable rows={5} cols={6} wrap={false} />
                </td>
              </tr>
            ) : rows.length === 0 && !creating ? (
              <tr>
                <td colSpan={7} className="py-8">
                  <EmptyState
                    icon={Link2}
                    title={hasActiveFilter ? 'No mappings match.' : 'No mappings yet.'}
                    body={
                      hasActiveFilter
                        ? 'Try clearing the filters.'
                        : 'Click “Add mapping” to create one.'
                    }
                  />
                </td>
              </tr>
            ) : (
              rows.map((row) =>
                editingId === row._id ? (
                  <EditRow
                    key={row._id}
                    draft={editDraft}
                    setDraft={setEditDraft}
                    availableSkus={availableSkus}
                    onSave={() => commitEdit(row)}
                    onCancel={cancelEdit}
                    pending={updateMutation.isPending}
                  />
                ) : (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="py-2 px-3 text-ink-700">{row.provider}</td>
                    <td className="py-2 px-3 font-mono text-xs text-ink-800">
                      {row.externalProductId}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-ink-500">
                      {row.externalOfferId || '—'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-ink-800">{row.externalLabel || '—'}</div>
                      {row.notes && <div className="text-xs text-ink-400">{row.notes}</div>}
                    </td>
                    <td className="py-2 px-3">
                      <SkuChip slug={row.internalSkuSlug} skus={availableSkus} />
                    </td>
                    <td className="py-2 px-3">
                      {row.isActive ? (
                        <span className="pill bg-emerald-50 text-emerald-700">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="pill bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => beginEdit(row)}
                        title="Edit"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                      <button
                        type="button"
                        className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
                        disabled={deleteMutation.isPending}
                        onClick={() => maybeDelete(row)}
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function emptyDraft() {
  return {
    provider: 'launchpad',
    externalProductId: '',
    externalOfferId: '',
    externalLabel: '',
    internalSkuSlug: '',
    notes: '',
    isActive: true,
  };
}

function EditRow({ draft, setDraft, availableSkus, isCreate, onSave, onCancel, pending }) {
  return (
    <tr className="bg-amber-50/40">
      <td className="py-2 px-3">
        <select
          className="input text-xs"
          value={draft.provider}
          onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
        >
          <option value="launchpad">launchpad</option>
          <option value="warriorplus">warriorplus</option>
          <option value="jvzoo">jvzoo</option>
          <option value="thrivecart">thrivecart</option>
          <option value="manual">manual</option>
        </select>
      </td>
      <td className="py-2 px-3">
        <input
          className="input text-xs font-mono"
          placeholder="e.g. 2148"
          value={draft.externalProductId}
          onChange={(e) => setDraft({ ...draft, externalProductId: e.target.value })}
        />
      </td>
      <td className="py-2 px-3">
        <input
          className="input text-xs font-mono"
          placeholder="(optional)"
          value={draft.externalOfferId}
          onChange={(e) => setDraft({ ...draft, externalOfferId: e.target.value })}
        />
      </td>
      <td className="py-2 px-3">
        <input
          className="input text-xs"
          placeholder="Human label"
          value={draft.externalLabel}
          onChange={(e) => setDraft({ ...draft, externalLabel: e.target.value })}
        />
      </td>
      <td className="py-2 px-3">
        <select
          className="input text-xs"
          value={draft.internalSkuSlug}
          onChange={(e) => setDraft({ ...draft, internalSkuSlug: e.target.value })}
        >
          <option value="">— pick a SKU —</option>
          {availableSkus.map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.displayName} ({s.slug}) — ${(s.priceCents / 100).toFixed(2)}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 px-3">
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          Active
        </label>
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <button
          type="button"
          className="btn-primary text-xs"
          disabled={pending}
          onClick={onSave}
          title={isCreate ? 'Create' : 'Save'}
        >
          <Save className="w-3 h-3" /> {isCreate ? 'Create' : 'Save'}
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>
          <X className="w-3 h-3" /> Cancel
        </button>
      </td>
    </tr>
  );
}

function SkuChip({ slug, skus }) {
  const sku = skus.find((s) => s.slug === slug);
  return (
    <span className="pill bg-brand-50 text-brand-700">
      {sku?.displayName || slug}
      <span className="text-ink-400 ml-1 text-xs">({slug})</span>
    </span>
  );
}
